import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";
import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut,
  type Auth
} from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";
import { currentUserSchema, firebaseWebConfigSchema, type CurrentUser } from "@eaf/contracts";
import { EAF_SESSION_HEADER } from "@eaf/auth";
import { publicApi } from "../api/client";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { setSessionId, type AppDispatch, type RootState } from "../state/store";
import { useQueryClient } from "@tanstack/react-query";
import { readSessionValue } from "../lib/session-draft";

interface AuthContextValue {
  state: "loading" | "anonymous" | "authenticated" | "forbidden" | "error";
  currentUser: CurrentUser | null;
  message: string | null;
  signIn(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  authorizedFetch<T>(path: string, init?: RequestInit): Promise<T>;
  authorizedStream(
    path: string,
    onData: (value: unknown) => void,
    signal: AbortSignal
  ): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthContextValue["state"]>("loading");
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const verifiedUid = useRef<string | null>(null);
  const sessionId = useSelector((root: RootState) => root.session.sessionId);
  const dispatch = useDispatch<AppDispatch>();
  const cache = useQueryClient();
  const clearPrivateCache = useCallback(() => {
    const filter = {
      predicate: (query: { queryKey: readonly unknown[] }) =>
        /^(admin|curator)-/.test(String(query.queryKey[0]))
    };
    void cache.cancelQueries(filter);
    cache.removeQueries(filter);
  }, [cache]);

  const request = useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      if (!auth?.currentUser) throw new Error("Authentication is required.");
      const token = await auth.currentUser.getIdToken();
      const headers = new Headers(init.headers);
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      if (sessionId) headers.set(EAF_SESSION_HEADER, sessionId);
      const response = await fetch(`/api/v1${path}`, { ...init, headers });
      const payload = (await response.json().catch(() => null)) as {
        ok?: boolean;
        data?: T;
        error?: { message?: string };
      } | null;
      if (!response.ok || !payload?.ok)
        throw new Error(payload?.error?.message ?? "The protected service is unavailable.");
      return payload.data as T;
    },
    [auth, sessionId]
  );

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let verification = 0;
    let disposed = false;
    void publicApi("/config/firebase", firebaseWebConfigSchema)
      .then((config) => {
        if (disposed) return;
        const firebaseApp =
          getApps()[0] ??
          initializeApp({
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
            appId: config.appId,
            ...(config.storageBucket ? { storageBucket: config.storageBucket } : {}),
            ...(config.messagingSenderId ? { messagingSenderId: config.messagingSenderId } : {}),
            ...(config.measurementId ? { measurementId: config.measurementId } : {})
          });
        const nextAuth = getAuth(firebaseApp);
        setAuth(nextAuth);
        unsubscribe = onIdTokenChanged(nextAuth, (firebaseUser) => {
          const attempt = ++verification;
          if (!firebaseUser) {
            clearPrivateCache();
            verifiedUid.current = null;
            setCurrentUser(null);
            dispatch(setSessionId(null));
            setState("anonymous");
            return;
          }
          const renewing = verifiedUid.current === firebaseUser.uid;
          setMessage(null);
          if (!renewing) {
            clearPrivateCache();
            setCurrentUser(null);
            setState("loading");
          }
          void firebaseUser
            .getIdToken()
            .then(async (token) => {
              const headers: Record<string, string> = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              };
              const existing = readSessionValue("eafSessionId");
              if (existing) headers[EAF_SESSION_HEADER] = existing;
              const response = await fetch("/api/v1/auth/session", { method: "POST", headers });
              const payload = (await response.json().catch(() => null)) as {
                ok?: boolean;
                data?: unknown;
                error?: { message?: string };
              } | null;
              if (disposed || attempt !== verification) return;
              if (response.status >= 500 || response.status === 429)
                throw new Error("Session verification temporarily unavailable");
              if (!response.ok || !payload?.ok) {
                clearPrivateCache();
                verifiedUid.current = null;
                setCurrentUser(null);
                dispatch(setSessionId(null));
                setMessage(payload?.error?.message ?? "This identity does not have EAF access.");
                setState(response.status === 403 ? "forbidden" : "error");
                return;
              }
              const user = currentUserSchema.parse(payload.data);
              verifiedUid.current = firebaseUser.uid;
              dispatch(setSessionId(user.sessionId));
              setCurrentUser(user);
              setMessage(null);
              setState("authenticated");
            })
            .catch(() => {
              if (disposed || attempt !== verification) return;
              setMessage("Authentication could not be verified.");
              if (!renewing) setState("error");
            });
        });
      })
      .catch(() => {
        setMessage("Authentication configuration is unavailable.");
        setState("error");
      });
    return () => {
      disposed = true;
      verification++;
      unsubscribe?.();
    };
  }, [dispatch, clearPrivateCache]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      currentUser,
      message,
      signIn: async (email, password) => {
        if (!auth) throw new Error("Authentication is not ready.");
        await signInWithEmailAndPassword(auth, email, password);
      },
      logout: async () => {
        if (currentUser)
          await request("/auth/logout", { method: "POST", body: "{}" }).catch(() => undefined);
        if (auth) await signOut(auth);
        clearPrivateCache();
        dispatch(setSessionId(null));
        setCurrentUser(null);
        setState("anonymous");
      },
      authorizedFetch: request,
      authorizedStream: async (path, onData, signal) => {
        await fetchEventSource(`/api/v1${path}`, {
          signal,
          openWhenHidden: true,
          fetch: async (url, init) => {
            if (!auth?.currentUser) throw new Error("Authentication is required.");
            const headers = new Headers(init?.headers);
            headers.set("Authorization", `Bearer ${await auth.currentUser.getIdToken()}`);
            if (sessionId) headers.set(EAF_SESSION_HEADER, sessionId);
            return fetch(url, { ...init, headers });
          },
          onopen: (response) => {
            if (
              !response.ok ||
              !response.headers.get("content-type")?.startsWith("text/event-stream")
            )
              throw new Error("Progress connection is unavailable. Your analysis remains saved.");
            return Promise.resolve();
          },
          onmessage: (event) => {
            if (event.data) onData(JSON.parse(event.data));
          },
          onerror: (error: unknown) => {
            throw error;
          }
        });
      }
    }),
    [auth, currentUser, dispatch, message, request, state, sessionId, clearPrivateCache]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
